import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { frameStore } from 'apps/components/Frame/appManager';

import { routerStore } from 'core';
import { analytics } from 'core/analytics';
import { useAppManager } from 'core/hooks';

import { Icon, Menu } from 'components';
import Heading from 'components/Heading';
import Tooltip from 'components/Tooltip';
import CollaboratorSettings from 'libs/rendering/components/header/collaboratorsSettings';

import { imageSrc } from 'apps/file/utils';
import { Avatar } from 'apps/components/shared';
import { conf as mainConf } from 'apps/config.yaml';
import { GroupsAppManager } from 'apps/group/appManager';

import {
  MainCollaboratorsWrapper,
  CircleWrapper,
  CirclesRowWrapper,
  TotalWrapper,
  IconWrapper,
  NoOtherCollaboratorsButton,
} from 'libs/rendering/components/header/collaborators.styled';
import { staticURL } from 'core/url';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';
import { useDesktopCompatibility } from 'hooks/useDesktopCompatible';
import { FrameHeaderHeight, SubHeaderHeight } from 'styles/constants';

const ActivityIndicator = observer(props => {
  const { status, profile } = props?.activity;

  const tooltipContent =
    status === 'INVITED'
      ? `${profile.username} &#8212; invite sent.`
      : profile.username;

  const tooltipId = `activty-tooltip-${profile.id}`;

  return (
    <CircleWrapper
      data-tip={tooltipContent}
      data-for={tooltipId}
      status={status}
    >
      <Tooltip id={tooltipId} delayShow={500} html place='bottom' />
      <Avatar
        noOpacity
        src={imageSrc(
          profile.avatar,
          profile.imageFallbackPath,
          mainConf.imgSizes.profile.small,
        )}
      />
    </CircleWrapper>
  );
});

ActivityIndicator.propTypes = {
  activity: PropTypes.object,
};

const Collaborators = observer(props => {
  const { usersActivity, maxUsers, sharedFileManager } = props;
  const groupsManager = useAppManager(GroupsAppManager, null, props);
  const { project } = frameStore;
  const { isRestricted } = sharedFileManager?.sharedFileQuery?.result || {};
  const canAccess = !project?.isRestricted && !isRestricted;
  const desktopMode = isInDesktopMode();
  const compatibleWithTab = useDesktopCompatibility({
    feature: 'tab',
  });

  useEffect(() => {
    if (usersActivity?.length) {
      frameStore.SetCurrentUsers(usersActivity.map(item => item.profile));
    }

    return () => {
      frameStore.SetCurrentUsers([]);
    };
  }, [usersActivity]);

  const settingsIconRef = useRef(null);
  const slicedUsersList = usersActivity.slice(0, maxUsers);
  const shouldShowParticipantList = !['make-part.file'].includes(
    routerStore.route.name,
  );

  let content = null;

  const { initiative, profile } = routerStore.route.params;

  const spaceSlug = initiative || profile;

  const shouldShowSettings =
    (sharedFileManager.sharedFileQuery?.result?.canUpdate && canAccess) ||
    false;

  const { userSettingsOpen } = sharedFileManager || {};

  useEffect(() => {
    if (spaceSlug && userSettingsOpen) {
      groupsManager.QueryTeamGroup(spaceSlug);
    }
  }, [spaceSlug, userSettingsOpen]);

  const handleClick = () => {
    analytics.sendEvent('invite-share-file');
    analytics.sendEvent('invite-share-file-CAD-rooms');
    sharedFileManager.SetUserSettingsOpen(true);
  };

  const handleMenuClose = () => {
    sharedFileManager.SetUserSettingsOpen(false);
  };

  const handleCloseBtnClick = () => {
    sharedFileManager.SetUserSettingsOpen(false);
  };

  if (usersActivity.length === 1) {
    content = (
      <div className='hover:opacity-50'>
        <NoOtherCollaboratorsButton
          onClick={handleClick}
          ref={settingsIconRef}
          data-testid='open-collaborator-menu-button'
        >
          Share
          <img src={staticURL('assets/icons/share.svg')} width={16} />
        </NoOtherCollaboratorsButton>
      </div>
    );
  } else {
    content = (
      <>
        {shouldShowParticipantList && (
          <CirclesRowWrapper data-testid='participant-list'>
            {slicedUsersList.map((activity, index) => (
              <ActivityIndicator key={index} activity={activity} />
            ))}
          </CirclesRowWrapper>
        )}
        {usersActivity.length > maxUsers && (
          <TotalWrapper>
            <Tooltip id='tooltipTotal' delayShow={200} html place='bottom' />
            <Heading
              level='5'
              size='xsmall'
              data-tip='Total of users'
              data-for='tooltipTotal'
            >
              {usersActivity.length}
            </Heading>
          </TotalWrapper>
        )}
        {shouldShowSettings ? (
          <div className='hover:opacity-50'>
            <IconWrapper
              ref={settingsIconRef}
              onClick={handleClick}
              data-testid='open-collaborator-menu-button'
            >
              <Icon name='user_settings' colorPath='wfBlack' />
            </IconWrapper>
          </div>
        ) : (
          <div className='mr-4' />
        )}
      </>
    );
  }

  return (
    <MainCollaboratorsWrapper>
      {content}
      <Menu
        open={sharedFileManager.userSettingsOpen}
        anchorEl={settingsIconRef.current}
        onClose={handleMenuClose}
        placement='customize-position'
        customizePosition={({ left, modalWidth, width, modalStyle }) => {
          return {
            left: `calc(${left - modalWidth + width}px + ${
              modalStyle.borderWidth
            })`,
            top:
              desktopMode && compatibleWithTab
                ? FrameHeaderHeight
                : FrameHeaderHeight + SubHeaderHeight,
          };
        }}
        sx={{
          top: '64px',
          boxShadow: '0px 2px 2px gray',
        }}
      >
        <CollaboratorSettings
          manager={sharedFileManager}
          closeMenu={handleCloseBtnClick}
          getRepeatUsers={invitedUsers => {
            return groupsManager.getRepeatInvitationUsers(invitedUsers);
          }}
          usersActivity={usersActivity}
          spaceSlug={spaceSlug}
          isProject={true}
        />
      </Menu>
    </MainCollaboratorsWrapper>
  );
});

Collaborators.propTypes = {
  usersActivity: PropTypes.arrayOf(
    PropTypes.shape(ActivityIndicator.propTypes),
  ),
  maxUsers: PropTypes.number,
};

export default Collaborators;
